/* eslint-disable react/jsx-pascal-case */
import React, { useState, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import OfferFinancialServicesSummary from './OfferFinancialServicesSummary';
import OfferFinancialServicesListComponentExtendedFields from './OfferFinancialServicesListComponentExtendedFields';
import EuroSymbol from '@mui/icons-material/EuroSymbol';
import { RightsContext } from 'providers/RightsContext';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import { FakeText } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';

export const OfferFinancialServicesCardExtendedFields = ({
  document,
  documentId,
  siteId,
  wheelSizes,
  validationInfo,
  saving,
  isDirty,
  onServiceSelectionChange,
  onHeaderPropertyChange,
  onSave,
  servicesLoading,
  documentLoaded,
  showModal,
  onServiceItemPropertyChange,
  onServiceItemComputeValue,
  onCloseAvailableServicesModal,
  onAddServices,
  editable,
  deleteService,
  handleModifyData,
  offerTypeId,
  handleCancelModifyData,
  allowedContractTypesForAddingServices
}) => {
  const { t } = useTranslation();

  const { financialServicesWrite } = useContext(RightsContext);
  const [state, setState] = useState({
    expanded: false,
    prevSaving: false
  });

  if (saving !== state.prevSaving) {
    setState({
      prevSaving: saving,
      expanded: !saving && !isDirty ? true : state.expanded
    });
  }

  const toggleExpanded = () => {
    setState({ ...state, expanded: !state.expanded });
  };

  const getHeaderItems = document => {
    if (document && document.services && document.categoryServices) {
      let itemsFromServices = document.services
        .filter(a => a.selected)
        .map(a => ({
          id: a.serviceTypeId,
          name: a.name,
          serviceMonthlyAmount: a.serviceMonthlyAmount,
          errorMessage: a.errorMessage
        }));
      let itemsFromCategories = document.categoryServices
        .filter(a => a.isSelected)
        .map(a => ({ id: a.serviceCategoryId, name: a.name, serviceMonthlyAmount: a.serviceMonthlyAmount }));
      let items = itemsFromServices.concat(itemsFromCategories);

      return items;
    }
  };

  const headerItems = useMemo(() => getHeaderItems(document), [document]);

  return (
    <CollapseOrBlankCard
      id={'offerFinancialServicesCard'}
      icon={EuroSymbol}
      expanded={document?.services?.length === 0 && !financialServicesWrite ? false : state.expanded}
      canExpand={true}
      onToggle={toggleExpanded}
      title={t('OfferEdit.FinancialServices.Title')}
      subheader={
        <OfferFinancialServicesSummary items={headerItems} currencyCode={document.header.currencyCode} allServices={document?.services} />
      }
      hideSubheaderOnExpand
    >
      {!documentLoaded ? (
        <FakeText lines={5} />
      ) : (
        <OfferFinancialServicesListComponentExtendedFields
          document={document}
          documentId={documentId}
          siteId={siteId}
          wheelSizes={wheelSizes}
          validationInfo={validationInfo}
          onServiceSelectionChange={onServiceSelectionChange}
          onHeaderPropertyChange={onHeaderPropertyChange}
          onSave={onSave}
          saving={saving}
          isDirty={isDirty}
          showModal={showModal}
          servicesLoading={servicesLoading}
          documentLoaded={documentLoaded}
          onCloseAvailableServicesModal={onCloseAvailableServicesModal}
          onAddServices={onAddServices}
          deleteService={deleteService}
          onServiceItemPropertyChange={onServiceItemPropertyChange}
          onServiceItemComputeValue={onServiceItemComputeValue}
          editable={editable}
          handleModifyData={handleModifyData}
          handleCancelModifyData={handleCancelModifyData}
          currencyCode={document.header.currencyCode}
          offerTypeId={offerTypeId}
          allowedContractTypesForAddingServices={allowedContractTypesForAddingServices}
        />
      )}
    </CollapseOrBlankCard>
  );
};

OfferFinancialServicesCardExtendedFields.propTypes = {
  t: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isDirty: PropTypes.bool.isRequired,
  document: PropTypes.object.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  wheelSizes: PropTypes.array.isRequired,
  validationInfo: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  showModal: PropTypes.bool.isRequired,
  onServiceSelectionChange: PropTypes.func.isRequired,
  onHeaderPropertyChange: PropTypes.func.isRequired,
  servicesLoading: PropTypes.array.isRequired,
  documentLoaded: PropTypes.bool.isRequired,
  onServiceItemPropertyChange: PropTypes.func.isRequired,
  onServiceItemComputeValue: PropTypes.func.isRequired,
  editable: PropTypes.bool.isRequired,
  handleModifyData: PropTypes.func.isRequired,
  handleCancelModifyData: PropTypes.func.isRequired,
  onCloseAvailableServicesModal: PropTypes.func.isRequired,
  onAddServices: PropTypes.func.isRequired,
  deleteService: PropTypes.func.isRequired,
  offerTypeId: PropTypes.number.isRequired,
  allowedContractTypesForAddingServices: PropTypes.string.isRequired
};

export default OfferFinancialServicesCardExtendedFields;
